import LinkButton from '@carvana/ds-link-button';
import cx from 'classnames';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Text } from '@/vdp/styles';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useConfiguration } from '~/context/common/StaticContext';
import getIsClampEnabled from '~/utilities/getIsClampEnabled';
import { CarouselCard } from './WhyBuy.styles';

type OwnProps = {
  bulletPoint: {
    title?: string | null;
    content?: string | null;
  };
  isClamped: boolean;
  setIsClamped: Dispatch<SetStateAction<boolean>>;
};

export default function WhyBuyCarouselCard({
  bulletPoint,
  isClamped = true,
  setIsClamped
}: OwnProps) {
  const { shouldUseTitleCasing } = useConfiguration();
  const trackEvent = useTrackEvent();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isClampEnabled, setIsClampEnabled] = useState(false);

  const toggleClamp = () => {
    setIsClamped(prev => !prev);
    trackEvent('Why Buy Feature', {
      description: 'why buy feature',
      item: `show ${isClamped ? 'more' : 'less'} truncated why buy feature`
    });
  };

  useEffect(() => {
    const el = textRef.current;

    if (!el) return;

    let prevWidth: number;

    const resizeObserver = new ResizeObserver(() => {
      if (el.clientWidth === prevWidth) return;

      setIsClampEnabled(getIsClampEnabled(el));

      prevWidth = el.clientWidth;
    });

    resizeObserver.observe(el);

    setIsClampEnabled(getIsClampEnabled(el));

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <CarouselCard>
      <div
        ref={textRef}
        className={cx(
          'line-clamp-6',
          isClampEnabled && !isClamped && 'line-clamp-none'
        )}
      >
        <h3 className="mb-lg text-text-strong t-heading-sm">
          {bulletPoint.title}
        </h3>
        <Text>{bulletPoint.content}</Text>
      </div>
      {isClampEnabled && (
        <LinkButton
          aria-expanded={!isClamped}
          size="sm"
          className="mt-auto inline-block text-left text-text-strong"
          data-adobe-id={null}
          data-analytics-id={null}
          data-testid="why-buy-show-more"
          onClick={toggleClamp}
        >
          {isClamped
            ? shouldUseTitleCasing
              ? 'Show More'
              : 'Show more'
            : shouldUseTitleCasing
            ? 'Show Less'
            : 'Show less'}
        </LinkButton>
      )}
    </CarouselCard>
  );
}
